import {
  atLeast, type CallTranscript, type Role, type TranscriptListItem, type TranscriptRep,
  type TranscriptSegment, type TranscriptSource, type TranscriptSpeaker,
} from "@/lib/types";
import { formatDateTime, formatDuration, formatPhoneNumber } from "@/lib/utils";

/**
 * The rules for a stored transcript under its recording (`features/call-transcript.tsx`). Pure,
 * for `test/transcription.test.ts`.
 *
 * The panel has three things to decide that the API does not decide for it: whether there is
 * anything to show at all, whether the reader may ask for a pass, and which line the playhead is
 * on. Each one has a wrong answer that looks right, which is why they are written down here and
 * not at the call site.
 */

/** How often an open transcript asks again while its whole-file job decodes. The job waits its
 *  turn in a queue and then decodes the whole file, so a faster poll mostly re-reads "pending". */
export const TRANSCRIPT_POLL_MS = 5_000;

/** Poll while the job is pending, and only then — a finished transcript does not change. */
export function transcriptPollInterval(transcript: CallTranscript | null | undefined): number | false {
  return transcript?.recording_status === "pending" ? TRANSCRIPT_POLL_MS : false;
}

/**
 * Who may ask for a whole-file pass. Mirrors the role floor on core-api's
 * `POST /calls/{id}/transcribe`, which is what actually refuses it: a pass spends decode CPU the
 * whole tenant shares, so it is a supervisor's call, while reading the result is anyone's who can
 * see the call.
 */
export function canTranscribe(role: Role | undefined): boolean {
  return atLeast(role, "supervisor");
}

export type TranscribeAction = "transcribe" | "again";

/**
 * The button the panel offers, or null.
 *
 * None while a job is pending: a second press would queue a second decode of the same file, and
 * the "Transcribing…" line already says one is on its way. None without a recording the reader
 * can play, because the route 404s on exactly that. "Transcribe again" wherever a pass has run
 * or left lines — including a failed one, which is the case it is most wanted for.
 */
export function transcribeAction(
  transcript: CallTranscript | null | undefined,
  { role, hasRecording }: { role: Role | undefined; hasRecording: boolean },
): TranscribeAction | null {
  if (!transcript || !hasRecording || !canTranscribe(role)) return null;
  if (transcript.recording_status === "pending") return null;
  return transcript.recording_status === null && transcript.recording.length === 0
    ? "transcribe"
    : "again";
}

export type TranscriptNotice =
  | { kind: "pending"; text: string }
  | { kind: "failed"; text: string };

/** The one line about the whole-file job, or null when there is nothing to say about it. */
export function transcriptNotice(transcript: CallTranscript | null | undefined): TranscriptNotice | null {
  if (transcript?.recording_status === "pending") return { kind: "pending", text: "Transcribing…" };
  if (transcript?.recording_status === "failed") {
    // The error is about the file — "no speech found", "could not decode" — and the reader's
    // next question is whether asking again would help, which the file's problem answers.
    const why = transcript.recording_error?.trim();
    return {
      kind: "failed",
      text: why ? `Could not transcribe the recording: ${why}` : "Could not transcribe the recording.",
    };
  }
  return null;
}

/**
 * Whether the panel renders nothing at all.
 *
 * Most calls on a tenant that does not transcribe have no lines and no job, and an empty box
 * saying so under every recording is noise that reads as a fault. So nothing — unless there is a
 * job to report on, or a button this reader may press.
 */
export function transcriptIsBlank(
  transcript: CallTranscript | null | undefined,
  action: TranscribeAction | null,
): boolean {
  if (action !== null || transcriptNotice(transcript) !== null) return false;
  return !transcript || (transcript.live.length === 0 && transcript.recording.length === 0);
}

/**
 * The line being spoken at `seconds`, or null.
 *
 * The last line to have STARTED, rather than the one whose window contains the playhead: the
 * gaps between turns are silence, and a highlight that blinks off in every pause reads as a bug.
 * Segments arrive ordered by start time, so this is a scan and not a search.
 *
 * Compared on whole seconds, because that is how both are shown: a line stamped `0:05` started
 * at 5.76 s, and the clock reads `0:05` from 5.0 s. Comparing the raw times would leave that
 * line unmarked while the clock already says its stamp, which reads as the highlight running a
 * line behind on every short turn.
 */
export function activeAt(segments: readonly TranscriptSegment[], seconds: number): number | null {
  const now = Math.floor(seconds);
  let found: number | null = null;
  for (const segment of segments) {
    if (Math.floor(segment.start_seconds) > now) break;
    found = segment.seq;
  }
  return found;
}

/** `m:ss` from the start of the capture. Not `formatDuration`: this is a position, not a length,
 *  and it has to read the same way the audio element's own clock does. */
export function transcriptStamp(seconds: number): string {
  const whole = Math.max(0, Math.floor(seconds));
  return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, "0")}`;
}

/**
 * Named for the leg the audio arrived on, which is all the capture knows — not for the call's
 * parties. A call is recorded on one of its legs, and whose voice that leg carries depends on how
 * the call was placed: an inbound caller's, a dialed lead's, or a rep's own when the console rang
 * them first. The row names both parties and not which of them that is, so a name here would be
 * right on most calls and confidently wrong on the rest. The call record above says who the two
 * people were.
 */
export const SPEAKER_LABELS: Record<TranscriptSpeaker, string | null> = {
  near: "This side",
  far: "Other side",
  // A mono capture: one mixed track, nobody to attribute a line to.
  unknown: null,
};

/** What each label means, for its tooltip. */
export const SPEAKER_HINTS: Record<TranscriptSpeaker, string | undefined> = {
  near: "The leg the audio was captured on",
  far: "The party that leg was speaking to",
  unknown: undefined,
};

// --- a contact's Transcripts tab (ADR-081 in platform) --------------------------------------------

/**
 * What names one transcript on the tab: its source and its id together. The id alone does not,
 * because a call transcribed both ways can store its two passes under one uuid — a dialer call
 * recorded on the lead's leg and transcribed live there too.
 */
export function transcriptKey(t: { id: string; source: TranscriptSource }): string {
  return `${t.source}:${t.id}`;
}

/** Where a transcript came from, as its header says it. */
export const SOURCE_TITLES: Record<TranscriptSource, string> = {
  recording: "Transcript from the recording",
  live: "Transcript captured live during the call",
};

/** One of our phones, as a person reads it: "Rep One (1001)", or the extension alone. */
export function repLabel(rep: TranscriptRep): string {
  return rep.name ? `${rep.name} (${rep.extension})` : rep.extension;
}

/** "A", "A and B", "A, B and C". */
function listed(parts: readonly string[]): string {
  if (parts.length <= 1) return parts[0] ?? "";
  return `${parts.slice(0, -1).join(", ")} and ${parts[parts.length - 1]}`;
}

/** The contact a transcript is copied under: the record's name, when it has one, and number. */
export interface TranscriptContact {
  name: string | null;
  phone: string;
}

/** The first line of a copied transcript: who the call was with. */
export function transcriptTitle(contact: TranscriptContact): string {
  const number = formatPhoneNumber(contact.phone);
  return contact.name ? `Call with ${contact.name}, ${number}` : `Call with ${number}`;
}

/**
 * The second line: when, how long, the campaign, and our phones on the call.
 *
 * `when` is a parameter so a test can pin it: the console prints times on the reader's own clock,
 * and a test run's clock is whatever machine it is on.
 */
export function transcriptFacts(
  item: TranscriptListItem,
  when: (iso: string | null) => string = formatDateTime,
): string {
  const facts = [when(item.started_at)];
  if (item.duration_seconds !== null) facts.push(`Length ${formatDuration(item.duration_seconds)}`);
  if (item.campaign_name) facts.push(item.campaign_name);
  else if (item.campaign_id) facts.push("Deleted campaign");
  if (item.reps.length > 0) {
    facts.push(`${item.reps.length === 1 ? "Rep" : "Reps"}: ${listed(item.reps.map(repLabel))}`);
  }
  return facts.join(" · ");
}

/** One line as it is copied: its time into the call, who spoke when the capture knows, the words. */
export function transcriptLineText(segment: TranscriptSegment): string {
  const label = SPEAKER_LABELS[segment.speaker] ?? null;
  return `${transcriptStamp(segment.start_seconds)} ${label ? `${label}: ` : ""}${segment.text.trim()}`;
}

/**
 * One transcript as plain text: a three-line header saying which call it is, a blank line, then a
 * line per turn. The header names the contact although the tab is theirs, because the text is read
 * somewhere else — a CRM note, an email, a document — and plain, because markup would arrive there
 * as stray symbols.
 */
export function formatTranscript(
  contact: TranscriptContact,
  item: TranscriptListItem,
  lines: readonly TranscriptSegment[],
  when?: (iso: string | null) => string,
): string {
  return [
    transcriptTitle(contact),
    transcriptFacts(item, when),
    SOURCE_TITLES[item.source],
    "",
    ...lines.map(transcriptLineText),
  ].join("\n");
}

/** What goes between two transcripts copied together: a line that cannot be mistaken for speech. */
export const TRANSCRIPT_DIVIDER = "-".repeat(40);

/** Several transcripts as one text, in the order given, with the divider between them. */
export function formatTranscripts(
  contact: TranscriptContact,
  docs: readonly { item: TranscriptListItem; lines: readonly TranscriptSegment[] }[],
  when?: (iso: string | null) => string,
): string {
  return docs
    .map(({ item, lines }) => formatTranscript(contact, item, lines, when))
    .join(`\n\n${TRANSCRIPT_DIVIDER}\n\n`);
}

/**
 * Newest call first, as the tab lists them: the order ticked transcripts are copied in, whatever
 * order they were ticked in. A row with no start sorts last, as the server sorts it.
 */
export function newestFirst<T extends Pick<TranscriptListItem, "started_at" | "id" | "source">>(
  items: readonly T[],
): T[] {
  const at = (item: T) => (item.started_at ? Date.parse(item.started_at) : Number.NEGATIVE_INFINITY);
  return [...items].sort(
    (a, b) => at(b) - at(a) || b.id.localeCompare(a.id) || b.source.localeCompare(a.source),
  );
}

/**
 * What a copy says it did, because a clipboard gives no sign of what is on it. A transcript the
 * reader may no longer read is left out by the server, and the copy says so rather than passing
 * off fewer calls as all of them.
 */
export function copiedMessage({
  copied, asked,
}: {
  /** Transcripts that went onto the clipboard. */
  copied: number;
  /** Transcripts the copy asked for. */
  asked: number;
}): { title: string; description?: string } {
  const count = (n: number) => `${n.toLocaleString("en-US")} transcript${n === 1 ? "" : "s"}`;
  if (copied === 0) {
    return { title: "Nothing was copied", description: "Those transcripts can no longer be read." };
  }
  if (copied < asked) {
    const missed = asked - copied;
    return {
      title: `Copied ${count(copied)}`,
      description: `${count(missed)} could not be copied: ${missed === 1 ? "it is" : "they are"} no longer yours to read.`,
    };
  }
  return { title: `Copied ${count(copied)}` };
}
